/**
 * Bottom cluster bar: one chip per category cluster (People, Research,
 * Technology, PLA, Procurement, Suppliers, Agencies) with counts. Clicking a
 * chip expands/collapses that cluster's highest-value nodes in the graph —
 * cluster-first rendering so hundreds of nodes never appear at once.
 */
import { FC } from 'react';
import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { IntelCluster, IntelClusterKey } from '../../types/intelligenceGraph';
import { NODE_TYPE_META } from './nodeStyles';

interface GraphClusterBarProps {
  clusters: IntelCluster[];
  expandedClusters: Set<IntelClusterKey>;
  onToggleCluster: (key: IntelClusterKey) => void;
}

export const GraphClusterBar: FC<GraphClusterBarProps> = ({
  clusters,
  expandedClusters,
  onToggleCluster,
}) => {
  if (clusters.length === 0) return null;
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ px: 2, py: 1, overflowX: 'auto' }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
        CLUSTERS
      </Typography>
      {clusters.map((cluster) => {
        const meta = NODE_TYPE_META[cluster.nodeType];
        const expanded = expandedClusters.has(cluster.key);
        return (
          <Tooltip
            key={cluster.key}
            title={
              expanded
                ? `Collapse ${cluster.label} cluster`
                : `Expand top ${cluster.label} nodes`
            }
          >
            <Chip
              size="small"
              clickable
              variant={expanded ? 'filled' : 'outlined'}
              onClick={() => onToggleCluster(cluster.key)}
              icon={<Icon icon={meta.icon} color={meta.color} width={14} />}
              label={`${cluster.label} ${cluster.count}`}
              sx={{
                borderColor: meta.color,
                ...(expanded && {
                  backgroundColor: `${meta.color}33`,
                }),
              }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
};
